import React, { useContext, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import { BSDA_WASTE_CODES, pluralize, WASTE_FAMILY_CODES } from "@td/constants";
import {
  BsdaConsistence,
  BsdaType,
  Query,
  QueryCompanyInfosArgs,
  CompanyType,
  BsdaPackaging
} from "@td/codegen-ui";
import { SealedFieldsContext } from "../../../../Dashboard/Creation/context";
import DisabledParagraphStep from "../../DisabledParagraphStep";
import WasteRadioGroup from "../../../../Forms/Components/WasteRadioGoup/WasteRadioGroup";
import { WasteCodeSelector } from "../../../../../form/registry/common/WasteCodeSelector";
import RhfPackagingList from "../../../../Forms/Components/PackagingList/RhfPackagingList";
import { bsdaPackagingTypes } from "../../../../Forms/Components/PackagingList/helpers";
import { RhfTagsInputWrapper } from "../../../../Forms/Components/TagsInput/TagsInputWrapper";
import NonScrollableInput from "../../../../common/Components/NonScrollableInput/NonScrollableInput";
import SelectableWasteTableWrapper from "../components/SelectableWasteTableWrapper";
import { InlineError } from "../../../../common/Components/Error/Error";
import Alert from "@codegouvfr/react-dsfr/Alert";
import Input from "@codegouvfr/react-dsfr/Input";
import Select from "@codegouvfr/react-dsfr/Select";
import { ToggleSwitch } from "@codegouvfr/react-dsfr/ToggleSwitch";
import RadioButtons from "@codegouvfr/react-dsfr/RadioButtons";
import {
  getInitialCompany,
  initialTransporter
} from "../../../../common/data/initialState";
import Tooltip from "@codegouvfr/react-dsfr/Tooltip";

const BSDA_COMPANY_INFOS = gql`
  query CompanyInfos($siret: String!) {
    companyInfos(siret: $siret) {
      siret
      name
      address
      companyTypes
    }
  }
`;

const WasteBsda = () => {
  const { siret } = useParams<{ siret: string }>();
  const methods = useFormContext();
  const { register, setValue, watch, formState } = methods;

  const { data, loading, error } = useQuery<
    Pick<Query, "companyInfos">,
    QueryCompanyInfosArgs
  >(BSDA_COMPANY_INFOS, {
    variables: { siret },
    fetchPolicy: "no-cache"
  });

  const id = watch("id", null);
  const waste = watch("waste", {});
  const sealedFields = useContext(SealedFieldsContext);
  const bsdaType = watch("type");
  const weight = watch("weight", {});
  const packagings = watch("packagings");

  useEffect(() => {
    if (bsdaType !== BsdaType.Gathering) {
      setValue("grouping", []);
    }
    if (bsdaType !== BsdaType.Reshipment) {
      setValue("forwarding", null);
    }
    if ([BsdaType.Reshipment, BsdaType.Gathering].includes(bsdaType)) {
      setValue("worker.company", getInitialCompany());
    }
    if (bsdaType === BsdaType.Collection_2710) {
      setValue("destination.company.siret", data?.companyInfos.siret);
      setValue("destination.company.address", data?.companyInfos.address);
      setValue("destination.company.name", data?.companyInfos.name);
      setValue("worker.company", getInitialCompany());
      // Nettoie les données transporteurs en gardant quand même un
      // transporteur vide par défaut au cas où on repasse sur un autre type
      // de BSDA. Un clean sera fait au moment de la soumission du formulaire
      // pour s'assurer que `transporters: []` en cas de collecte en déchetterie.
      setValue("transporters", [initialTransporter]);
    }
  }, [bsdaType, setValue, data]);

  const isWasteCenter = data?.companyInfos.companyTypes?.includes(
    "WASTE_CENTER" as CompanyType
  );

  if (loading) return <p>Loading...</p>;
  if (error) return <InlineError apolloError={error} />;

  const isDechetterie = bsdaType === BsdaType.Collection_2710;
  const sealNumbersLength = waste?.sealNumbers?.length || 0;
  const isSubjectToADR =
    waste?.isSubjectToADR === null || Boolean(waste?.isSubjectToADR); // Legacy compatibility, null means true

  const quantityLength: number = packagings.reduce(
    (acc: number, packaging: BsdaPackaging) =>
      acc + (Number(packaging.quantity) || 0),
    0
  );

  return (
    <>
      {!!sealedFields.length && <DisabledParagraphStep />}
      <div className="fr-col">
        <WasteRadioGroup
          title="Type de bordereau"
          legend="J'édite un BSDA pour :"
          disabled={sealedFields.includes("type")}
          options={[
            {
              label: "La collecte d'amiante sur un chantier",
              nativeInputProps: {
                ...register("type"),
                value: BsdaType.OtherCollections
              }
            },
            {
              label:
                "Le groupement de déchets entreposés sur un site relevant de la rubrique 2718 (ou 2710-1)",
              nativeInputProps: {
                ...register("type"),
                value: BsdaType.Gathering
              }
            },
            {
              label: "La réexpédition après entreposage provisoire",
              nativeInputProps: {
                ...register("type"),
                value: BsdaType.Reshipment
              }
            },
            ...(isWasteCenter
              ? [
                  {
                    label: "La collecte en déchetterie",
                    nativeInputProps: {
                      ...register("type"),
                      value: BsdaType.Collection_2710
                    }
                  }
                ]
              : [])
          ]}
        />
      </div>

      <div className="fr-col">
        {bsdaType === BsdaType.Gathering && (
          <>
            <Alert
              description="Sélectionnez des BSDA ayant le même code déchet et le même
                        exutoire"
              severity="info"
              small
              className="fr-mb-2w"
            />

            <SelectableWasteTableWrapper type={bsdaType} bsdaId={id} />
          </>
        )}

        {bsdaType === BsdaType.Reshipment && (
          <>
            <SelectableWasteTableWrapper type={bsdaType} bsdaId={id} />

            <Alert
              description="Vous effectuez une réexpédition. Les informations sur le déchet ont été automatiquement reportées et ne sont pas modifiables."
              severity="info"
              small
              className="fr-mt-2w"
            />
            <h4 className="fr-h4 fr-mt-4w">Mention ADR</h4>

            <Input
              className="fr-col-md-8"
              label="Mention au titre de la réglementation ADR (optionnel)"
              disabled={sealedFields.includes(`waste.adr`)}
              nativeInputProps={{
                ...register("waste.adr"),
                placeholder: waste?.adr || "Exemple: Transport sous 1.1.3.6"
              }}
              state={formState.errors.waste?.["adr"] ? "error" : "default"}
              stateRelatedMessage={formState.errors.waste?.["adr"]?.message}
            />

            <Alert
              description="Vous pouvez modifier la mention ADR..."
              severity="info"
              small
              className="fr-mt-2w"
            />
          </>
        )}

        {bsdaType !== BsdaType.Reshipment && (
          <>
            <h4 className="fr-h4 fr-mt-4w">Déchet</h4>

            <WasteCodeSelector
              name={"waste.code"}
              methods={methods}
              whiteList={[...BSDA_WASTE_CODES]}
            />

            {!waste?.code && formState?.errors?.waste?.["code"] && (
              <p className="fr-text--sm fr-error-text fr-mb-4v">
                {formState?.errors?.waste?.["code"]?.message}
              </p>
            )}

            <Select
              className="fr-col-md-8 fr-mt-2w"
              label="Code famille"
              nativeSelectProps={{
                ...register("waste.familyCode")
              }}
              state={
                formState.errors.waste?.["familyCode"] ? "error" : "default"
              }
              stateRelatedMessage={
                formState.errors.waste?.["familyCode"]?.message
              }
              disabled={sealedFields.includes(`waste.familyCode`)}
            >
              <option value="">Sélectionnez une valeur</option>
              {WASTE_FAMILY_CODES.map(item => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>

            <Input
              className="fr-col-md-8"
              label="Dénomination usuelle"
              disabled={sealedFields.includes(`waste.materialName`)}
              nativeInputProps={{
                ...register("waste.materialName")
              }}
              state={
                formState.errors.waste?.["materialName"] ? "error" : "default"
              }
              stateRelatedMessage={
                formState.errors.waste?.["materialName"]?.message
              }
            />

            <ToggleSwitch
              label={
                <span>
                  Le déchet contient des{" "}
                  <a
                    className="fr-link force-external-link-content force-underline-link"
                    href="https://www.ecologique-solidaire.gouv.fr/polluants-organiques-persistants-pop"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    polluants organiques persistants
                  </a>
                </span>
              }
              disabled={sealedFields.includes(`waste.pop`)}
              checked={Boolean(waste.pop)}
              onChange={(checked: boolean) => {
                setValue("waste.pop", checked);
              }}
              className="fr-mt-4w"
            />

            <ToggleSwitch
              label="Le déchet est soumis à l'ADR"
              disabled={sealedFields.includes(`waste.isSubjectToADR`)}
              checked={isSubjectToADR}
              onChange={(checked: boolean) => {
                setValue("waste.isSubjectToADR", checked);
                if (!checked) {
                  setValue("waste.adr", null);
                }
              }}
              className="fr-mt-4w"
            />

            {isSubjectToADR && (
              <Input
                className="fr-col-md-8 fr-pl-9w fr-mt-1w"
                label="Mention au titre de la réglementation ADR"
                disabled={sealedFields.includes(`waste.adr`)}
                nativeInputProps={{
                  ...register("waste.adr")
                }}
                state={formState.errors.waste?.["adr"] ? "error" : "default"}
                stateRelatedMessage={formState.errors.waste?.["adr"]?.message}
              />
            )}

            <Input
              className="fr-col-md-8 fr-mt-4w"
              label="Mention au titre des règlements RID, ADNR, IMDG (optionnel)"
              disabled={sealedFields.includes(`waste.nonRoadRegulationMention`)}
              nativeInputProps={{
                ...register("waste.nonRoadRegulationMention")
              }}
              state={
                formState.errors.waste?.["nonRoadRegulationMention"] && "error"
              }
              stateRelatedMessage={
                (formState.errors.waste?.["nonRoadRegulationMention"]
                  ?.message as string) ?? ""
              }
            />

            <h4 className="fr-h4 fr-mt-4w">Conditionnement</h4>
            <RhfPackagingList
              disabled={sealedFields.includes(`packagings`)}
              fieldName="packagings"
              packagingTypes={bsdaPackagingTypes}
            />
            {formState?.errors?.packagings && (
              <p className="fr-text--sm fr-error-text fr-mb-4v">
                {formState?.errors?.packagings?.message as string}
              </p>
            )}
            <h4 className="fr-h4 fr-mt-4w">Consistance</h4>

            <RadioButtons
              disabled={sealedFields.includes(`waste.consistence`)}
              orientation="horizontal"
              state={
                formState.errors.waste?.["consistence"] ? "error" : "default"
              }
              stateRelatedMessage={
                formState.errors.waste?.["consistence"]?.message
              }
              options={[
                {
                  label: "Solide",
                  nativeInputProps: {
                    onChange: () =>
                      setValue("waste.consistence", BsdaConsistence.Solide),

                    checked: waste.consistence === BsdaConsistence.Solide
                  }
                },
                {
                  label: "Pulvérulent",
                  nativeInputProps: {
                    onChange: () =>
                      setValue(
                        "waste.consistence",
                        BsdaConsistence.Pulverulent
                      ),

                    checked: waste.consistence === BsdaConsistence.Pulverulent
                  }
                },
                {
                  label: "Pâteux",
                  nativeInputProps: {
                    onChange: () =>
                      setValue("waste.consistence", BsdaConsistence.Pateux),

                    checked: waste.consistence === BsdaConsistence.Pateux
                  }
                },
                {
                  label: "Autre",
                  nativeInputProps: {
                    onChange: () =>
                      setValue("waste.consistence", BsdaConsistence.Other),

                    checked: waste.consistence === BsdaConsistence.Other
                  }
                }
              ]}
            />

            {waste.consistence === BsdaConsistence.Other && (
              <Input
                disabled={sealedFields.includes(`waste.consistenceDescription`)}
                className="fr-col-md-8 fr-mt-4w"
                label="Si Autre, préciser :"
                nativeInputProps={{
                  ...register("waste.consistenceDescription")
                }}
                state={
                  formState.errors.waste?.["consistenceDescription"] && "error"
                }
                stateRelatedMessage={
                  (formState.errors.waste?.["consistenceDescription"]
                    ?.message as string) ?? ""
                }
              />
            )}

            <h4 className="fr-h4 fr-mt-4w">Quantité remise</h4>

            <div className="fr-grid-row fr-grid-row--gutters fr-mt-4w">
              <Input
                className="fr-col-md-3"
                label="En nombre"
                disabled={true}
                nativeInputProps={{
                  value: quantityLength
                }}
              />

              <div className="fr-col-md-3">
                <NonScrollableInput
                  label="Poids total en tonnes"
                  disabled={sealedFields.includes("weight.value")}
                  nativeInputProps={{
                    inputMode: "decimal",
                    step: "0.000001",
                    type: "number",
                    ...register("weight.value")
                  }}
                  state={formState.errors?.weight?.["value"] && "error"}
                  stateRelatedMessage={
                    (formState.errors?.weight?.["value"]?.message as string) ??
                    ""
                  }
                />

                <p className="fr-info-text fr-mt-5v">
                  Soit {(weight.value || 0) * 1000} kg
                </p>
              </div>

              <div className="fr-col-md-6">
                <RadioButtons
                  legend="Cette quantité est"
                  disabled={sealedFields.includes("weight.isEstimate")}
                  orientation="horizontal"
                  state={formState.errors?.weight?.["isEstimate"] && "error"}
                  stateRelatedMessage={
                    (formState.errors?.weight?.["isEstimate"]
                      ?.message as string) ?? ""
                  }
                  options={[
                    {
                      label: "Réelle",
                      nativeInputProps: {
                        onChange: () => setValue("weight.isEstimate", false),

                        checked: weight.isEstimate === false
                      }
                    },
                    {
                      label: (
                        <span>
                          Estimée{" "}
                          <Tooltip
                            className="fr-ml-1w"
                            title={`"Quantité estimée conformément à l'article 5.4.1.1.3.2 de l'ADR" si soumis`}
                          />
                        </span>
                      ),
                      nativeInputProps: {
                        onChange: () => setValue("weight.isEstimate", true),
                        checked: weight.isEstimate === true
                      }
                    }
                  ]}
                />
              </div>
            </div>

            {!isDechetterie && (
              <>
                <h4 className="fr-h4 fr-mt-4w">Numéros de scellés</h4>

                <RhfTagsInputWrapper
                  disabled={sealedFields.includes(`waste.sealNumbers`)}
                  label="Numéros"
                  fieldName={"waste.sealNumbers"}
                />

                <p className="fr-info-text">
                  Vous avez saisi {sealNumbersLength}{" "}
                  {pluralize("numéro", sealNumbersLength)} pour{" "}
                  {Number(quantityLength)}{" "}
                  {pluralize("conditionnement", quantityLength)}
                </p>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default WasteBsda;
